import { useState, useCallback, useEffect, type FC } from 'react';
import { useCustomMutation } from '@refinedev/core';
import { PenTool, FileText, Zap, Linkedin, Mail } from 'lucide-react';
import { GuidedTour } from '@/components/onboarding/GuidedTour';
import { usePageTour } from '@/hooks/usePageTour';
import { contentFactoryTourSteps } from '@/lib/tour-data';
import StatCard from '@/components/common/StatCard';
import ContentInputForm, { type ContentFormData } from './components/ContentInputForm';
import ContentResultTabs, { type GeneratedContent } from './components/ContentResultTabs';

interface Stats {
  generated: number;
  tokens: number;
  linkedin: number;
  emails: number;
}

const ENDPOINT_MAP: Record<string, string> = {
  linkedin: '/api/v1/admin/growth/content/social-post',
  email: '/api/v1/admin/growth/content/email',
  thread: '/api/v1/admin/growth/content/thread',
};

const TOUR_ID = 'admin-content-factory';

const ContentFactoryPage: FC = () => {
  const tour = usePageTour(TOUR_ID);
  useEffect(() => {
    const handler = () => tour.startTour();
    window.addEventListener('restart-page-tour', handler);
    return () => window.removeEventListener('restart-page-tour', handler);
  }, [tour]);

  const [results, setResults] = useState<GeneratedContent>({});
  const [stats, setStats] = useState<Stats>({ generated: 0, tokens: 0, linkedin: 0, emails: 0 });
  const [lastFormData, setLastFormData] = useState<ContentFormData | null>(null);
  const [regeneratingTab, setRegeneratingTab] = useState<'linkedin' | 'email' | 'thread' | null>(null);

  const { mutate: generateAll, isLoading: isGenerating } = useCustomMutation();
  const { mutate: regenerateSingle } = useCustomMutation();

  const buildPayload = (formData: ContentFormData) => ({
    topic: formData.topic,
    persona: formData.persona !== 'general' ? formData.persona : undefined,
    context: formData.context || undefined,
  });

  const handleGenerate = useCallback(
    (formData: ContentFormData) => {
      setLastFormData(formData);
      generateAll(
        {
          url: '/api/v1/admin/growth/content/generate-all',
          method: 'post',
          values: buildPayload(formData),
        },
        {
          onSuccess: (response) => {
            const d = response?.data?.data ?? response?.data ?? {};
            setResults({ linkedinPost: d.linkedinPost, email: d.email, thread: d.thread, totalTokens: d.totalTokens });
            setStats((prev) => ({
              generated: prev.generated + 3,
              tokens: prev.tokens + (d.totalTokens ?? 0),
              linkedin: prev.linkedin + 1,
              emails: prev.emails + 1,
            }));
          },
        },
      );
    },
    [generateAll],
  );

  const handleRegenerate = useCallback(
    (format: 'linkedin' | 'email' | 'thread') => {
      if (!lastFormData) return;
      setRegeneratingTab(format);

      regenerateSingle(
        { url: ENDPOINT_MAP[format], method: 'post', values: buildPayload(lastFormData) },
        {
          onSuccess: (response) => {
            const d = response?.data?.data ?? response?.data ?? {};
            setResults((prev) => {
              if (format === 'linkedin') return { ...prev, linkedinPost: d };
              if (format === 'email') return { ...prev, email: d };
              return { ...prev, thread: d };
            });
            setStats((prev) => ({
              ...prev,
              generated: prev.generated + 1,
              tokens: prev.tokens + (d.tokens ?? 0),
            }));
            setRegeneratingTab(null);
          },
          onError: () => setRegeneratingTab(null),
        },
      );
    },
    [lastFormData, regenerateSingle],
  );

  const hasResults = !!(results.linkedinPost?.content || results.email?.content || results.thread?.content || results.thread?.tweets?.length);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="fc-gradient-header rounded-2xl p-6 text-white">
        <div className="flex items-center gap-3" data-tour="content-factory-header">
          <PenTool className="w-6 h-6" />
          <div>
            <h1 className="text-xl font-bold">Content Factory</h1>
            <p className="mt-0.5 text-xs italic text-blue-200">Usine a contenu</p>
            <p className="mt-0.5 text-sm text-blue-100">
              Generez du contenu marketing avec l'IA — LinkedIn, Email, Thread
            </p>
          </div>
        </div>
      </div>

      {/* StatCards */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard title="Contenus générés" value={stats.generated} icon={FileText} iconColor="text-blue-600 dark:text-blue-400" iconBg="bg-blue-50 dark:bg-blue-900/30" />
        <StatCard title="Tokens utilisés" value={stats.tokens} icon={Zap} iconColor="text-amber-600 dark:text-amber-400" iconBg="bg-amber-50 dark:bg-amber-900/30" />
        <StatCard title="LinkedIn posts" value={stats.linkedin} icon={Linkedin} iconColor="text-sky-600 dark:text-sky-400" iconBg="bg-sky-50 dark:bg-sky-900/30" />
        <StatCard title="Emails générés" value={stats.emails} icon={Mail} iconColor="text-emerald-600 dark:text-emerald-400" iconBg="bg-emerald-50 dark:bg-emerald-900/30" />
      </div>

      {/* Form */}
      <ContentInputForm onSubmit={handleGenerate} isLoading={isGenerating} />

      {/* Results */}
      {hasResults && (
        <ContentResultTabs results={results} onRegenerate={handleRegenerate} isRegenerating={regeneratingTab} />
      )}
      <GuidedTour tourId={TOUR_ID} steps={contentFactoryTourSteps} isActive={tour.isActive} onComplete={tour.completeTour} onSkip={tour.skipTour} />
    </div>
  );
};

export default ContentFactoryPage;
